import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Single type-ahead "Where are you based?" input. Autocompletes to "City, ST".
 * Prefills from IP geolocation (always user-correctable). onChange receives the
 * structured location object (or null while unselected).
 */
export default function LocationInput({ value, onChange, idPrefix = "location", className = "" }) {
    const [text, setText] = useState(value?.display || "");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [prefilled, setPrefilled] = useState(false);
    const debounce = useRef(null);
    const boxRef = useRef(null);

    useEffect(() => {
        if (value || text) return;
        let alive = true;
        api.geoIp().then(res => {
            if (alive && res?.location) {
                setText(res.location.display);
                setPrefilled(true);
                onChange(res.location);
            }
        }).catch(() => {});
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const close = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const onType = (v) => {
        setText(v);
        setPrefilled(false);
        onChange(null);
        clearTimeout(debounce.current);
        if (v.trim().length < 2) { setItems([]); setOpen(false); return; }
        debounce.current = setTimeout(async () => {
            try {
                const res = await api.geoSuggest(v.trim());
                setItems(res.items || []);
                setOpen((res.items || []).length > 0);
            } catch { /* network hiccup — keep quiet */ }
        }, 200);
    };

    const pick = (loc) => {
        onChange(loc);
        setText(loc.display);
        setPrefilled(false);
        setOpen(false);
    };

    return (
        <div ref={boxRef} className="relative">
            <input
                data-testid={`${idPrefix}-input`}
                type="text"
                autoComplete="off"
                placeholder="Start typing your city…"
                value={text}
                onChange={e => onType(e.target.value)}
                onFocus={() => items.length > 0 && setOpen(true)}
                className={className || "w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"}
            />
            {prefilled && value && (
                <div className="mt-1 text-[10px] tracking-[0.14em] uppercase text-ink-muted" data-testid={`${idPrefix}-prefill-note`}>
                    Guessed from your connection — edit if that's not right
                </div>
            )}
            {open && (
                <ul
                    data-testid={`${idPrefix}-suggestions`}
                    className="absolute z-40 left-0 right-0 mt-1 bg-cream border border-burgundy/20 shadow-lg max-h-56 overflow-y-auto"
                >
                    {items.map(loc => (
                        <li key={`${loc.city}-${loc.state}`}>
                            <button
                                type="button"
                                data-testid={`${idPrefix}-option-${loc.city.toLowerCase().replace(/\s+/g, "-")}-${loc.state.toLowerCase()}`}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => pick(loc)}
                                className="w-full text-left px-3 py-2.5 text-sm text-ink hover:bg-burgundy/[0.06]"
                            >
                                <span className="text-burgundy font-medium">{loc.city}</span>
                                <span className="text-ink-muted">, {loc.state}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
